import {ExpressCheckoutElement, useElements, useStripe} from '@stripe/react-stripe-js'
import type {
  ClickResolveDetails,
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementOptions,
} from '@stripe/stripe-js'
import Big from 'big.js'

import useIsAuthenticated from '@/auth/hooks/useIsAuthenticated'
import type {FlightBooking} from '@/lib/flight/flightModel'
import {bookFlight} from '@/lib/flight/flightService'
import {assistanceInformationAtom, passengersAndContactAtom} from '@/lib/flight/store'
import {useLocale} from '@/lib/locale/locale'
import {CLIENT_SECRET_FOR_0_AMOUNT_PAYMENT} from '@/lib/payment/constants'
import useCustomerSession from '@/lib/payment/hooks/useCustomerSession'
import {useSetElementsOptions} from '@/lib/payment/PaymentProvider'
import {getRoundUp, useRoundUp} from '@/lib/payment/store'
import toStripeAmountNumber from '@/lib/payment/utils/toStripeAmountNumber'
import {FlightCheckoutRoute, FlightDetailsRoute} from '@/routeRegistry'
import toastErrorMessage from '@/utils/errors/toastErrorMessage'
import useFlightSummary from '@/views/Flight/hooks/useFightSummary'

interface Props {
  className?: string
  submitFlightBooking: () => Promise<FlightBooking | undefined>
}

const options: StripeExpressCheckoutElementOptions = {
  paymentMethods: {
    amazonPay: 'auto',
    applePay: 'always',
    googlePay: 'always',
    link: 'auto',
    paypal: 'auto',
  },
  layout: {
    maxColumns: 1,
    overflow: 'never',
  },
}

export default memo(function ExpressFlightCheckout({className, submitFlightBooking}: Props) {
  const stripe = useStripe()
  const elements = useElements()
  const router = useRouter()
  const {flightId} = FlightDetailsRoute.useParams()
  const {applyRewards, coupon, option} = FlightDetailsRoute.useSearch()
  const latestApplyRewards = useLatest(applyRewards)
  const [locale] = useLocale()
  const latestLocale = useLatest(locale)
  const isAuthenticated = useIsAuthenticated()
  const latestIsAuthenticated = useLatest(isAuthenticated)
  const latestCoupon = useLatest(coupon)
  const store = useStore()
  const [roundUp] = useRoundUp()
  const latestRoundUp = useLatest(roundUp)
  const latestSubmitFlightBooking = useLatest(submitFlightBooking)

  const setElementsOptions = useSetElementsOptions()

  const {data: customerSession} = useCustomerSession()

  const {subtotal, flightDetails, total, derivedDiscountValue, roundUpAmount} = useFlightSummary(
    flightId,
    option,
    applyRewards,
    coupon,
    undefined,
  )
  const latestOption = useLatest(option)
  const latestFlightDetails = useLatest(flightDetails)
  const latestSubtotal = useLatest(subtotal)
  const latestDerivedDiscountValue = useLatest(derivedDiscountValue)
  const latestRoundUpAmount = useLatest(roundUpAmount)

  ;(function updateElementsOptions() {
    setElementsOptions(options => {
      options.mode = 'payment'
      options.setupFutureUsage = 'off_session' // vs on?
      if (customerSession) {
        options.customerSessionClientSecret = customerSession
      }
      options.amount = toStripeAmountNumber(total)
    })
  })()

  const onClick = useCallback(async (event: StripeExpressCheckoutElementClickEvent) => {
    const flightBooking = await latestSubmitFlightBooking.current()
    if (!flightBooking) return

    const expresslineItems = (() => {
      return [
        {
          name: 'Flight ticket',
          amount: toStripeAmountNumber(latestSubtotal.current),
        },
        latestRoundUp.current
          ? {
              name: 'Donate change to NFB',
              amount: toStripeAmountNumber(latestRoundUpAmount.current),
            }
          : null,
        {
          name: 'Discount',
          amount: toStripeAmountNumber(Big(-latestDerivedDiscountValue.current)),
        },
      ].filter(Boolean)
    })()

    const options: ClickResolveDetails = {
      emailRequired: true,
      phoneNumberRequired: true,
      lineItems: expresslineItems,
    }

    console.log('click', options)

    setTimeout(() => {
      event.resolve(options)
    }, 0)
  }, [])

  const onConfirm = useCallback(
    async (event: StripeExpressCheckoutElementConfirmEvent) => {
      if (!stripe || !elements) return

      try {
        const {error: submitError} = await elements.submit()
        if (submitError) {
          toastErrorMessage(submitError)
          console.error(submitError)
          return
        }

        // Create a ConfirmationToken using the details collected by the Express Checkout Element
        const {error: confirmationError, confirmationToken} = await stripe.createConfirmationToken({
          elements,
          params: {
            payment_method_data: {
              allow_redisplay: 'always',
            },
          },
        })

        const confirmationTokenId = confirmationToken?.id

        if (confirmationError || !confirmationTokenId) {
          toastErrorMessage(confirmationError)
          console.error(confirmationError)
          return
        }

        const returnHref = router.buildLocation({
          to: FlightCheckoutRoute.fullPath,
          search: {
            guest: !latestIsAuthenticated.current ? true : undefined,
          },
        }).href

        const assistance = store.get(assistanceInformationAtom)
        const passengersAndContact = store.get(passengersAndContactAtom)

        invariant(latestFlightDetails.current)

        const finalBookingId =
          latestFlightDetails.current.booking_options[latestOption.current]?.flight_id

        invariant(finalBookingId)

        const {clientSecret, booking} = await bookFlight({
          email: event.billingDetails?.email ?? '',
          confirmationToken: confirmationToken.id,
          coupon: latestCoupon.current,
          isAuthenticated: latestIsAuthenticated.current,
          data: passengersAndContact,
          flightId: finalBookingId,
          optionIdx: latestOption.current,
          returnHref,
          locale: latestLocale.current,
          assistance,
          applyRewards: latestApplyRewards.current,
          roundUp: getRoundUp(store),
        })

        if (clientSecret !== CLIENT_SECRET_FOR_0_AMOUNT_PAYMENT) {
          const returnUrlWithBookingId = router.buildLocation({
            to: FlightCheckoutRoute.fullPath,
            search: {
              guest: !latestIsAuthenticated.current ? true : undefined,
              bookingId: booking.id,
            },
          }).href

          // Confirm the PaymentIntent
          const {error: confirmError} = await stripe.confirmPayment({
            clientSecret,
            confirmParams: {
              confirmation_token: confirmationTokenId,
              return_url: location.origin + returnUrlWithBookingId,
            },
          })

          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- wrong library type
          if (confirmError) {
            console.log(confirmError)
          }
        }

        await router.navigate({
          to: FlightCheckoutRoute.fullPath,
          search: {
            guest: !latestIsAuthenticated.current ? true : undefined,
            bookingId: booking.id,
          },
        })
      } catch (e) {
        console.error(e)
        toastErrorMessage(e)
      }
    },
    [elements, router, store, stripe],
  )

  if (total.toNumber() <= 0) {
    return null
  }

  return (
    <div className={className}>
      <ExpressCheckoutElement options={options} onConfirm={onConfirm} onClick={onClick} />
    </div>
  )
})
